import { ArticulationType } from '@models/editor/Articulation'
import { asset } from '@utils/client'
import nfsyn from '@utils/nf-webaudio/nf-webaudio'
import { SampledInstrumentType } from '@utils/nf-webaudio/sampled-instrument'

// Typescript why do you do this??
interface fn {
  (param: SampledInstrumentZoneType): void
}

export interface SampledInstrumentZoneType {
  instrument: SampledInstrumentType
  minVelocity: number
  maxVelocity: number
  tuning: number
  gain: number
  start: number
  loopDecay: number
  loopStart: number
  loopEnd: number
  articulation: ArticulationType
  sampleRate: number | null
  loaded: boolean
  loading: boolean
  loadCallbacks: fn[]
  lowPitch: number
  highPitch: number
  name: string
  uri: string
  buffer: any
  rootPitch?: number
  cacheKey: () => string
  parseZone: (zone: Element) => void
  location: () => string
  load: (callback: () => void) => void
  match: (pitch: number, articulation: string) => boolean
  getShiftForNote: (pitch: number) => number
  ensureDuration: (duration: number) => void
}

// SampledInstrumentZone: representation of a single sample that is used
// to render some class of possible notes.
const SampledInstrumentZone = function (
  this: SampledInstrumentZoneType,
  instrument: SampledInstrumentType
): void {
  this.instrument = instrument
  this.minVelocity = 0
  this.maxVelocity = 127
  this.tuning = 0
  this.gain = 1
  this.start = 0
  this.loopDecay = 0
  this.loopStart = 0
  this.loopEnd = 0
  this.articulation = ArticulationType.NORMAL
  this.sampleRate = instrument.sampleRate
  this.loaded = false
  this.loading = false
  this.loadCallbacks = []
}

SampledInstrumentZone.prototype.location = function (this: SampledInstrumentZoneType): string {
  let uri = this.uri
  if (this.instrument.preview) {
    uri = ('preview/' + this.uri).replace('wav', 'mp3')
  }
  return asset(this.instrument.baseUri + uri)
}

SampledInstrumentZone.prototype.cacheKey = function (this: SampledInstrumentZoneType) {
  // We use the low and high pitches here because some names/uris may be used by multiple zones, and we want
  // to cache each of these zones separately.
  const pitchKey = '[' + this.lowPitch + ',' + this.highPitch + ']'
  return this.instrument.baseUri + (this.name || this.uri) + pitchKey
}

SampledInstrumentZone.prototype.load = function (
  this: SampledInstrumentZoneType,
  callback: () => void
) {
  // check to see if already loaded. If so, invoke this callback now
  if (this.loaded) {
    callback()
    return
  }

  // Note the callback request.
  this.loadCallbacks.push(callback)

  // If already loading, nothing more to do right now.
  if (this.loading) {
    return
  }
  this.loading = true

  nfsyn.utils.loadAudioBuffer(
    this.location(),
    this.instrument.synth.context,
    (buffer) => {
      this.buffer = buffer

      // trim off "start" region if specified AND there is no built-in support for starting the sample past the beginning
      if (this.start && !this.instrument.synth.supportsStart) {
        const trimmed = this.instrument.synth.context.createBuffer(
          this.buffer.numberOfChannels,
          this.buffer.length - this.start,
          this.buffer.sampleRate
        )
        for (let i = 0; i < this.buffer.numberOfChannels; i++) {
          trimmed.getChannelData(i).set(this.buffer.getChannelData(i).subarray(this.start))
        }
        this.buffer = trimmed
        if (this.loopEnd > 0) {
          this.loopStart -= this.start
          this.loopEnd -= this.start
        }
      }

      this.loaded = true
      // lol
      this.loadCallbacks.forEach((f) => f(this))
      this.loadCallbacks = []
    },
    (errorMessage) => {
      // Problem loading the sound.
      console.error('Loading instrument sample ' + this.location() + ' failed: ' + errorMessage)
      alert('Instrument loading error.')
    }
  )
}

SampledInstrumentZone.prototype.ensureDuration = function (
  this: SampledInstrumentZoneType,
  duration: number
) {
  if (this.loopEnd === 0 || this.buffer.duration >= duration) {
    return
  }

  // Determine the number of loop doublings needed to achieve this duration
  let loopLength = this.loopEnd - this.loopStart
  let doublings = 0
  let loopCount = 1
  let length = this.loopStart + loopLength
  while (length < Math.ceil(duration * this.buffer.sampleRate)) {
    length += loopCount * loopLength
    loopCount *= 2
    doublings++
  }

  // Now create the appropriately sized buffer and populate it by repeatedly doubling the loop body
  const extended = this.instrument.synth.context.createBuffer(
    this.buffer.numberOfChannels,
    length,
    this.buffer.sampleRate
  )
  for (let i = 0; i < this.buffer.numberOfChannels; i++) {
    extended.getChannelData(i).set(this.buffer.getChannelData(i))
  }
  while (doublings > 0) {
    for (let i = 0; i < this.buffer.numberOfChannels; i++) {
      extended
        .getChannelData(i)
        .set(extended.getChannelData(i).subarray(this.loopStart, this.loopEnd), this.loopEnd)
    }
    this.loopEnd += loopLength
    loopLength *= 2
    doublings--
  }
  this.buffer = extended
}

SampledInstrumentZone.prototype.parseZone = function (
  this: SampledInstrumentZoneType,
  zone: Element
) {
  // redeclartion so debugger has visibility
  const n = nfsyn

  n.utils.parseStringProperty(zone, 'uri', this)
  n.utils.parseStringProperty(zone, 'name', this)
  n.utils.parseStringProperty(zone, 'articulation', this)
  n.utils.parseProperty(parseInt, zone, 'sampleRate', this)
  n.utils.parseProperty(parseInt, zone, 'minVelocity', this)
  n.utils.parseProperty(parseInt, zone, 'maxVelocity', this)
  n.utils.parseProperty(parseFloat, zone, 'loopDecay', this)
  n.utils.parseProperty(parseFloat, zone, 'tuning', this)
  n.utils.parseProperty(parseFloat, zone, 'gain', this)
  n.utils.parseProperty(n.utils.parseNote, zone, 'rootPitch', this)
  n.utils.parseProperty(n.utils.parseNote, zone, 'lowPitch', this)
  n.utils.parseProperty(n.utils.parseNote, zone, 'highPitch', this)
  n.utils.parseProperty(parseInt, zone, 'start', this)
  n.utils.parseProperty(parseInt, zone, 'loopStart', this)
  n.utils.parseProperty(parseInt, zone, 'loopEnd', this)
}

// Determine the frequency shift to be applied to the zone's sample when some note pitch is rendered
SampledInstrumentZone.prototype.getShiftForNote = function (
  this: SampledInstrumentZoneType,
  pitch: number
) {
  const cents = this.tuning + this.instrument.masterTuning + (pitch - (this.rootPitch || 0)) * 100.0
  return Math.exp((Math.LN2 * cents) / 1200.0)
}

// Determine whether some arbitrary note should be rendered by this zone
SampledInstrumentZone.prototype.match = function (
  this: SampledInstrumentZoneType,
  pitch: number,
  articulation: string
) {
  return (
    (articulation || ArticulationType.NORMAL) == this.articulation &&
    pitch >= this.lowPitch &&
    pitch <= this.highPitch
  )
}

export default SampledInstrumentZone
